import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import { type Theme } from "@mui/material/styles";
import { type RowComponentProps } from "react-window";

import EditableCell from "./EditableCell";
import Cell from "./Cell";
import CheckboxCell from "./CheckboxCell";

type StyleProps = {
  rowHeight?: number;
  handleRowClick?: any;
  disabled?: boolean;
};

const useStyles = makeStyles<Theme, StyleProps>(theme => ({
  row: props => ({
    height: props.rowHeight,
    display: "flex",
    overflow: "hidden",
    cursor: props.handleRowClick && !props.disabled ? "pointer" : undefined,
    "&:hover": {
      backgroundColor: props.disabled ? theme.palette.grey[200] : undefined,
    },
  }),
}));

type Props = RowComponentProps<{
  columns: any;
  selectable: any;
  handleRowClick: any;
  handleCheckbox: any;
  rowIdKey: any;
  getDOMId: any;
  rows: any[];
  rowHeight: number;
}>;

function Row({
  index,
  style,
  columns,
  selectable,
  handleRowClick,
  handleCheckbox,
  rowIdKey,
  getDOMId,
  rows,
  rowHeight,
}: Props) {
  const rowData = rows[index];
  const rowId = rowData[rowIdKey];

  const classes = useStyles({ rowHeight, handleRowClick, disabled: rowData.__selectDisabled });
  const rowRef = React.useRef<any>();

  return (
    <div
      style={style}
      className={classes.row}
      onClick={e => handleRowClick(rowId, e.shiftKey)}
      onMouseDown={handleMouseDown}
      tabIndex={0}
      id={getDOMId(index)}
      ref={rowRef}
    >
      {selectable && (
        <CheckboxCell
          selected={Boolean(rowData.__selected)}
          disabled={Boolean(rowData.__selectDisabled)}
          onClick={shiftKey => {
            rowRef.current?.focus();
            handleCheckbox(rowId, shiftKey);
          }}
        />
      )}
      {columns.map(col => {
        // Cannot edit a disabled cell
        const CellComponent = col.editable && !rowData.__selectDisabled ? EditableCell : Cell;

        return (
          <CellComponent
            width={col.width}
            flexGrow={col.flexGrow}
            flexShrink={col.flexShrink}
            key={col.dataKey}
            numeric={col.numeric}
            noEllipses={col.noEllipses}
            {...(col.onEdit && { onEdit: col.onEdit, rowData })}
            disabled={rowData.__selectDisabled}
          >
            {col.renderFn ? col.renderFn(rowData[col.dataKey], rowData) : rowData[col.dataKey]}
          </CellComponent>
        );
      })}
    </div>
  );
}

// prevents shift selection from selecting table text, but still sets focus
function handleMouseDown(e) {
  if (e.shiftKey) {
    e.preventDefault();
    e.currentTarget.focus();
  }
}

export default React.memo(Row);
